import {ref, computed, onMounted, onBeforeUnmount} from "vue";
import {DateTime} from "luxon";
import history from "@/assets/history.json";
import {getProgram} from "@/composables/useProgram";
import useServerClock from "@/composables/useServerClock";

const log = (msg) => console.log(`useOnlineSessions: ${msg}`);
const year = history.latestYear;
const timeZoneConference = history.years[year];

function getEventKey(category) {
  if (/masterclass/i.test(category)) return "masterclass";
  if (/workshop/i.test(category)) return "workshop";
  if (/showcase/i.test(category)) return "showcase";
  return null;
}

function makeDateTime(dateStr, timeStr) {
  const [dd, mm, yyyy] = dateStr.split("/");
  const [h, m] = timeStr.trim().split(":");
  return DateTime.fromObject(
    {year: +yyyy, month: +mm, day: +dd, hour: +h, minute: +m, second: 0},
    {zone: timeZoneConference}
  );
}

function parseTimeRange(timeStr, dateStr) {
  if (!timeStr || !timeStr.includes("-")) return null;
  const [startStr, endStr] = timeStr.split(/\s*-\s*/);
  return {start: makeDateTime(dateStr, startStr), end: makeDateTime(dateStr, endStr)};
}

// A row's times: a "17:00 - 17:15" range, or a duration in minutes after the previous row
function getRowTimes(timeStr, date, previousEnd) {
  const times = parseTimeRange(timeStr, date);
  if (times || !timeStr || !previousEnd) return times;
  const duration = parseInt(timeStr);
  return isNaN(duration) ? null : {start: previousEnd, end: previousEnd.plus({minutes: duration})};
}

// The check-in hour before each day ("Check-in + Platform Q&A", e.g. 16:00 - 17:00) is a
// warm-up, not a session: the countdown runs to the first real session (17:00). During the
// warm-up (WARM_UP_HOURS before a session) the countdown becomes a link to /live.
export const WARM_UP_HOURS = 1;

function getIsWarmUp(title) {
  return /check-in|platform q&a/i.test(title || "");
}

// Session rows have Category "Online"; the event comes from the day's heading row above them
// ("Online Masterclass", "Online Workshop: …"), or from the row's own Category if it names one
function parseOnlineOnlyEvents(program) {
  const col = (heading) => program[0].indexOf(heading);
  const events = [];
  let [currentDate, currentEventKey, previousEnd] = ["", null, null];
  for (const row of program.slice(1)) {
    currentDate = row[col("Date")] || currentDate;
    currentEventKey = getEventKey(row[col("Category")] || "") || currentEventKey;
    if (!currentDate || !currentEventKey) continue;
    if (!/online only/i.test(row[col("Mode")] || "")) continue;
    if (getIsWarmUp(row[col("Talk/Role/Event")])) continue;
    const times = getRowTimes(row[col("Duration/Time")] || "", currentDate, previousEnd);
    if (!times) continue;
    previousEnd = times.end;
    events.push({...times, eventKey: currentEventKey});
  }
  return events;
}

// Returns the first session that has not yet ended (no lookahead cap).
function findNextSession(events, now) {
  for (const event of events) {
    if (event.end < now) continue;
    return event;
  }
  return null;
}

// The events registration is for (masterclass, workshop) that still have a session ahead, in
// programme order: the banner's countdown needs the viewer registered for all of them
const REGISTRABLE_EVENTS = ["masterclass", "workshop"];
function getUpcomingEventKeys(events, now) {
  const upcoming = events.filter((event) => event.end >= now).map((event) => event.eventKey);
  return [...new Set(upcoming)].filter((key) => REGISTRABLE_EVENTS.includes(key));
}

// now: the server's time (useServerClock), so a session starts at the same moment for everyone,
// whatever the browser's clock says; no session until the server has answered
export default function useOnlineSessions() {
  const {getServerNow, isSynced} = useServerClock();
  const now = ref(getServerNow());
  const programData = ref(null);
  let tick = null;
  const onlineEvents = computed(() => {
    if (!programData.value?.length) return []; // no rows: the sheet didn't load
    return parseOnlineOnlyEvents(programData.value);
  });
  const activeSession = computed(() => {
    if (!isSynced.value) return null;
    return findNextSession(onlineEvents.value, now.value);
  });
  const upcomingEventKeys = computed(() => {
    if (!isSynced.value) return [];
    return getUpcomingEventKeys(onlineEvents.value, now.value);
  });
  const isLive = computed(() => {
    if (!activeSession.value) return false;
    return now.value >= activeSession.value.start;
  });
  const isWarmUp = computed(() => {
    if (!activeSession.value || isLive.value) return false;
    return now.value >= activeSession.value.start.minus({hours: WARM_UP_HOURS});
  });
  onMounted(async () => {
    log(`onMounted: year=${year}, timeZone=${timeZoneConference}`);
    try {
      programData.value = await getProgram(year);
      log(`program loaded: ${programData.value?.length} rows`);
    } catch (error) {
      console.error(`useOnlineSessions: failed to load program: ${error}`);
    }
    log(`onlineEvents after load: ${onlineEvents.value.length} online-only events`);
    if (onlineEvents.value.length) {
      const first = onlineEvents.value[0];
      log(`first event: key=${first.eventKey}, start=${first.start?.toISO()}, end=${first.end?.toISO()}`);
    }
    log(`activeSession: ${activeSession.value ? activeSession.value.eventKey : "null"}`);
    tick = setInterval(() => {
      now.value = getServerNow();
    }, 1000);
  });
  onBeforeUnmount(() => clearInterval(tick));
  return {activeSession, upcomingEventKeys, isLive, isWarmUp, now, year};
}
